import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import { forkJoin, of } from 'rxjs';
import { catchError, delay, switchMap, tap } from 'rxjs/operators';

import { FeatureToggleStateService } from '@account-users/account/state/feature-toggles/feature-toggle-state.service';

import { AccountService } from './account.service';
import { SmsCreditsService } from './sms-credits.service';
import {
  AccountPackageResponse,
  IAccount,
  LicensePackageCode,
  EssentialQuotas,
  LiteQuotas,
  AllInOneQuotas,
  FreemiumQuotas,
  GrowQuotas,
  AutomateQuotas,
  SendQuotas,
} from '../models';

@Injectable()
export class TrialAccountUpgradeService {
  constructor(
    private readonly accountService: AccountService,
    private readonly smsCreditService: SmsCreditsService,
    private readonly featureToggleStateService: FeatureToggleStateService,
    private toastr: ToastrService,
  ) {}

  public upgrade(
    accountDetails: IAccount,
    orderNumber: string,
    licensePackage: LicensePackageCode,
  ) {
    let emailQuota = 0;
    let allowedSeats = 0;
    let smsCredits = 0;
    let audienceProfiles = 0;

    switch (licensePackage) {
      case LicensePackageCode.Lite:
        emailQuota = LiteQuotas.EmailQuota;
        allowedSeats = LiteQuotas.UserSeats;
        smsCredits = LiteQuotas.SmsCredits;
        audienceProfiles = LiteQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Send:
        emailQuota = SendQuotas.EmailQuota;
        allowedSeats = SendQuotas.UserSeats;
        smsCredits = SendQuotas.SmsCredits;
        audienceProfiles = SendQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Essential:
        emailQuota = EssentialQuotas.EmailQuota;
        allowedSeats = EssentialQuotas.UserSeats;
        smsCredits = EssentialQuotas.SmsCredits;
        audienceProfiles = EssentialQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Automate:
        emailQuota = AutomateQuotas.EmailQuota;
        allowedSeats = AutomateQuotas.UserSeats;
        smsCredits = AutomateQuotas.SmsCredits;
        audienceProfiles = AutomateQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.AllInOne:
        emailQuota = AllInOneQuotas.EmailQuota;
        allowedSeats = AllInOneQuotas.UserSeats;
        smsCredits = AllInOneQuotas.SmsCredits;
        audienceProfiles = AllInOneQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Grow:
        emailQuota = GrowQuotas.EmailQuota;
        allowedSeats = GrowQuotas.UserSeats;
        smsCredits = GrowQuotas.SmsCredits;
        audienceProfiles = GrowQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Freemium:
        emailQuota = FreemiumQuotas.EmailQuota;
        allowedSeats = FreemiumQuotas.UserSeats;
        smsCredits = FreemiumQuotas.SmsCredits;
        audienceProfiles = FreemiumQuotas.AudienceProfiles;
        break;
    }

    return this.accountService
      .updateAccountLicensePackage(
        accountDetails?.id,
        orderNumber,
        licensePackage,
      )
      .pipe(
        switchMap((bootstrappedLicenses: AccountPackageResponse[]) => {
          this.featureToggleStateService.loadLicenses(accountDetails?.id, {});
          return of(true).pipe(
            delay(500),
            tap(() =>
              this.featureToggleStateService.updateLicense(
                accountDetails?.id,
                bootstrappedLicenses,
              ),
            ),
          );
        }),
        switchMap(() =>
          forkJoin([
            this.updateAccountDetails(accountDetails),
            this.updateEmailQuota(accountDetails?.id, orderNumber, emailQuota),
            this.updateSmsCredits(accountDetails?.id, orderNumber, smsCredits),
            this.updateAccountSettings(
              accountDetails?.id,
              orderNumber,
              allowedSeats,
              audienceProfiles,
            ),
          ]),
        ),
      );
  }

  private updateEmailQuota(
    accountId: string,
    orderNumber: string,
    emailQuota: number,
  ) {
    return this.accountService
      .addEmailQuota(accountId, orderNumber, {
        emailQuota,
        emailOneTimeQuota: 0,
      })
      .pipe(
        catchError((err) => {
          this.toastr.error(
            err?.error?.message || 'Failed to update an email quota',
          );
          return of(null);
        }),
      );
  }

  private updateAccountSettings(
    accountId: string,
    orderId: string,
    userSeats: number,
    audiencePackageSize: number,
  ) {
    return this.accountService
      .patchAccountSettings(
        {
          userSeats,
          audiencePackageSize,
          orderId,
        },
        accountId,
      )
      .pipe(
        catchError(() => {
          this.toastr.error(
            'Failed to update user seats and audience package size',
          );
          return of(null);
        }),
      );
  }

  private updateSmsCredits(
    accountId: string,
    orderId: string,
    smsCredits: number,
  ) {
    return this.smsCreditService
      .patchSmsCredits({ accountId, orderId, addCredits: smsCredits })
      .pipe(
        catchError(() => {
          this.toastr.error('Failed to update SMS Credits');
          return of(null);
        }),
      );
  }

  private updateAccountDetails(accountDetails: IAccount) {
    return this.accountService
      .updateAccountDetails(accountDetails?.id, accountDetails)
      .pipe(
        catchError(() => {
          this.toastr.error(`Failed to change category and subscription id`);
          return of(null);
        }),
      );
  }
}
